import dayjs from "dayjs";
import React from "react";

import { Calendar } from ".";

interface DeserializedDateRange {
  endDate: dayjs.Dayjs | null;
  startDate: dayjs.Dayjs | null;
}

interface Props {
  close: () => void;
  initialGridStart?: dayjs.Dayjs;
  allowPast?: boolean;
  earliestAllowed?: dayjs.Dayjs;
  noClear?: boolean;
  onChange: (start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => void | null;
  value: DeserializedDateRange;
}

const Controller: React.FC<Props> = ({
  onChange = null,
  close,
  earliestAllowed = dayjs(),
  initialGridStart = dayjs(),
  allowPast = false,
  noClear = false,
  value,
}) => {
  const [gridStart, setGridStart] = React.useState(dayjs());

  const onPrevious = () => {
    setGridStart(gridStart.clone().subtract(1, "month"));
  };

  const onNext = () => {
    setGridStart(gridStart.clone().add(1, "month"));
  };

  const onClear = () => {
    if (value.startDate !== null || value.endDate !== null) {
      setGridStart(initialGridStart.clone().startOf("month"));
      onChange && onChange(null, null);
    }
  };

  const onToday = () => {
    setGridStart(dayjs().startOf("month"));
  };

  const onSelect = (day: dayjs.Dayjs) => {
    if (!onChange) {
      return;
    }

    // If both start and end date are already selected,
    // the selected day is the new start date, and end date should be null
    if (value.startDate && value.endDate) {
      return onChange(day, null);
    }

    // If there no start date selected, or if there is one, but the newly
    // selected date comes before it, then we reset the start date
    if (!value.startDate || day.isBefore(value.startDate)) {
      return onChange(day, value.endDate);
    }

    // If there no end date selected, or if there is one, but the newly
    // selected date comes after it, then we reset the end date
    if (!value.endDate || day.isAfter(value.endDate)) {
      return onChange(value.startDate, day);
    }
  };

  const calendarProps = {
    clearHandler: onClear,
    close,
    nextHandler: onNext,
    previousHandler: onPrevious,
    todayHandler: onToday,
  };

  const gridProps = {
    gridStart,
    onSelect,
    selectionEnd: value.endDate,
    selectionStart: value.startDate,
  };

  const gridConfiguration = {
    allowPast,
    earliestAllowed,
    firstDayOfWeek: 1,
    noClear,
  };

  return <Calendar {...calendarProps} {...gridProps} {...gridConfiguration} />;
};

export default Controller;
